import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Link,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import BookmarksRoundedIcon from '@mui/icons-material/BookmarksRounded';
import {Link as RouterLink, useNavigate, useParams} from 'react-router';
import {browseSharedTag} from '../../../firebase/firestore/sharedTags';
import {useSession} from '../../../firebase/UserProvider';
import {coverageScoreColor, coverageTrackColor} from '../coverage/coverageScoreColor';
import {statusColor, statusLabel} from '../../collection/collectionTaskPayload';
import PriorityIndicator from '../components/PriorityIndicator';
import AuthorChip from '../../products/components/common/AuthorChip';
import useCollectionMaterialViewer from '../../collection/viewer/useCollectionMaterialViewer';
import TagBadge from '../../../components/documentContent/TagBadge';

/** Matches Section titles (e.g. "ICRs") — icon + subtitle1 bold. */
const TAG_HEADER_ICON_COLOR = 'rgba(156, 39, 176, .8)';

/** Entity-type icons: ICR/EEI reuse the /icr colors; the rest follow existing type colors. */
const SECTION_ICONS = {
    icr: {Icon: DescriptionOutlinedIcon, color: '#1565c0'},
    eei: {Icon: ListAltOutlinedIcon, color: '#00838f'},
    fintel: {Icon: ArticleOutlinedIcon, color: '#2e7d32'},
    gap: {Icon: HelpOutlineIcon, color: '#6d4c41'},
    ct: {Icon: AssignmentOutlinedIcon, color: '#e65100'},
    cm: {Icon: SourceOutlinedIcon, color: '#6a1b9a'},
};

function Section({sectionKey, title, count, children, empty}) {
    const {Icon, color} = SECTION_ICONS[sectionKey];
    return (
        <Stack spacing={1} data-testid={`section-${sectionKey}`}>
            <Stack direction="row" spacing={0.75} alignItems="center">
                <Icon data-testid={`section-icon-${sectionKey}`} sx={{fontSize: 18, color}}/>
                <Typography variant="subtitle1" fontWeight={700}>
                    {title} ({count})
                </Typography>
            </Stack>
            {empty ? (
                <Typography variant="body2" color="text.secondary">None</Typography>
            ) : children}
        </Stack>
    );
}

/** Fixed widths match the longest labels so Priority / Data / Analytic / Author align across rows. */
const PRIORITY_META_BADGE_WIDTH = '18ch'; // Medium Priority + icon
const DATA_COVERAGE_CHIP_WIDTH = '10ch'; // Data 100%
const ANALYTIC_COVERAGE_CHIP_WIDTH = '14ch'; // Analytic 100%

/**
 * Small percent chip ("Data NN%" / "Analytic NN%") aligned with the Data
 * Coverage / Analytic Coverage columns on the /icr list (same color bands).
 */
function CoverageChip({kind, pct}) {
    const has = pct != null;
    const width = kind === 'Analytic' ? ANALYTIC_COVERAGE_CHIP_WIDTH : DATA_COVERAGE_CHIP_WIDTH;
    return (
        <Tooltip title={has ? `${kind} coverage ${pct}%` : `No ${kind.toLowerCase()} coverage`} arrow>
            <Chip
                size="small"
                label={has ? `${kind} ${pct}%` : `${kind} —`}
                sx={{
                    height: 20,
                    width,
                    flexShrink: 0,
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    fontFamily: '"Roboto Mono", monospace',
                    bgcolor: has ? coverageTrackColor(pct) : '#eeeeee',
                    color: has ? coverageScoreColor(pct) : '#9e9e9e',
                    '& .MuiChip-label': {
                        px: 0.75,
                        width: '100%',
                        textAlign: 'center',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    },
                }}
            />
        </Tooltip>
    );
}

function AuthorCell({userId, displayName}) {
    if (!userId && !displayName) {
        return null;
    }
    return (
        <Box onClick={(e) => e.stopPropagation()} sx={{minWidth: 0}}>
            <AuthorChip
                userId={userId || null}
                userDisplayName={displayName || userId || 'Unknown'}
                flex={undefined}
            />
        </Box>
    );
}

/** Priority + Data/Analytic coverage + author — shared by ICR and EEI rows. */
function RequirementMeta({item}) {
    return (
        <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{flexWrap: 'nowrap'}}>
            <PriorityIndicator
                priority={item.priority}
                size="small"
                sx={{
                    width: PRIORITY_META_BADGE_WIDTH,
                    flexShrink: 0,
                    justifyContent: 'center',
                    '& .MuiChip-label': {
                        px: 0.75,
                        width: '100%',
                        textAlign: 'center',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    },
                }}
            />
            <CoverageChip kind="Data" pct={item.dataCoveragePct}/>
            <CoverageChip kind="Analytic" pct={item.analyticCoveragePct}/>
            <AuthorCell userId={item.authorUserId} displayName={item.author}/>
        </Stack>
    );
}

/**
 * CM/admin browse of all entities sharing a free-form tag.
 */
export default function TagBrowsePage() {
    const {tag: rawTag} = useParams();
    const tag = rawTag ? decodeURIComponent(rawTag) : '';
    const navigate = useNavigate();
    const {openCollectionMaterial} = useCollectionMaterialViewer();
    const {role, roles} = useSession();
    const canView = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!canView || !tag) return undefined;
        let cancelled = false;
        setLoading(true);
        browseSharedTag(tag)
            .then((d) => {
                if (!cancelled) {
                    setData(d);
                    setError(null);
                }
            })
            .catch((e) => {
                if (!cancelled) {
                    setError(e.message);
                    setData(null);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [tag, canView]);

    if (!canView) {
        return (
            <Container maxWidth="md" sx={{py: 3}}>
                <Alert severity="warning">Collection manager or admin role required to browse shared tags.</Alert>
            </Container>
        );
    }

    const icrs = data?.icrs || [];
    const eeis = data?.eeis || [];
    const fintels = data?.fintels || [];
    const gaps = data?.gaps || [];
    const collectionTasks = data?.collectionTasks || [];
    const collectionMaterials = data?.collectionMaterials || [];
    const tagLabel = data?.tag || tag;

    return (
        <Container maxWidth="md" sx={{py: 3}}>
            <Stack spacing={2.5}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                        <BookmarksRoundedIcon
                            data-testid="tag-header-icon"
                            sx={{fontSize: 18, color: TAG_HEADER_ICON_COLOR}}
                        />
                        <Typography variant="subtitle1" fontWeight={700}>
                            Tag
                        </Typography>
                    </Stack>
                    <TagBadge data-testid="tag-badge" tag={tagLabel}/>
                </Stack>

                {loading && <Typography color="text.secondary">Loading…</Typography>}
                {error && <Alert severity="error">{error}</Alert>}

                {data && (
                    <Paper variant="outlined" sx={{p: 2.5}}>
                        <Stack spacing={3}>
                            <Section sectionKey="icr" title="ICRs" count={icrs.length} empty={icrs.length === 0}>
                                <Stack spacing={1.25}>
                                    {icrs.map((item) => {
                                        const key = item.number || item.id;
                                        return (
                                            <Stack key={item.id} spacing={0.5}>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/icr/${encodeURIComponent(key)}`}
                                                    underline="hover"
                                                    variant="body1"
                                                    fontWeight={600}
                                                >
                                                    {item.number || item.id} — {item.title || 'ICR'}
                                                </Link>
                                                {item.workflowStatus && item.workflowStatus !== 'Active' && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.workflowStatus}
                                                    </Typography>
                                                )}
                                                <RequirementMeta item={item}/>
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Section>

                            <Section sectionKey="eei" title="EEIs" count={eeis.length} empty={eeis.length === 0}>
                                <Stack spacing={1.25}>
                                    {eeis.map((item) => (
                                        <Stack key={item.id} spacing={0.5}>
                                            <Link
                                                component={RouterLink}
                                                to={`/icr/eei/${encodeURIComponent(item.number || item.id)}`}
                                                underline="hover"
                                                variant="body1"
                                                fontWeight={600}
                                            >
                                                {item.number || item.id}
                                            </Link>
                                            <Typography variant="body2" color="text.secondary">
                                                {item.text || item.title}
                                            </Typography>
                                            <RequirementMeta item={item}/>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Section>

                            <Section
                                sectionKey="fintel"
                                title="FINTELs"
                                count={fintels.length}
                                empty={fintels.length === 0}
                            >
                                <Stack spacing={1.25}>
                                    {fintels.map((item) => (
                                        <Stack key={item.id} spacing={0.5}>
                                            <Link
                                                component={RouterLink}
                                                to={`/products/${encodeURIComponent(item.id)}/preview`}
                                                underline="hover"
                                                variant="body1"
                                                fontWeight={600}
                                            >
                                                {item.title || item.id}
                                            </Link>
                                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                <Typography
                                                    variant="caption"
                                                    fontFamily='"Roboto Mono", monospace'
                                                    color="text.secondary"
                                                >
                                                    {item.id}
                                                </Typography>
                                                <AuthorCell userId={item.authorUserId} displayName={item.author}/>
                                            </Stack>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Section>

                            <Section sectionKey="gap" title="Gaps" count={gaps.length} empty={gaps.length === 0}>
                                <Stack spacing={1.25}>
                                    {gaps.map((item) => {
                                        const key = item.number || item.id;
                                        return (
                                            <Stack key={item.id} spacing={0.25}>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/icr/gap/${encodeURIComponent(key)}`}
                                                    underline="hover"
                                                    variant="body1"
                                                    fontWeight={600}
                                                >
                                                    {item.number || item.id}
                                                    {item.text || item.title
                                                        ? ` — ${item.text || item.title}`
                                                        : ''}
                                                </Link>
                                                {item.reportId && (
                                                    <Link
                                                        component={RouterLink}
                                                        to={`/products/${encodeURIComponent(item.reportId)}/preview`}
                                                        underline="hover"
                                                        variant="caption"
                                                    >
                                                        {item.reportTitle || 'Open FINTEL'}
                                                    </Link>
                                                )}
                                                <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{flexWrap: 'nowrap'}}>
                                                    <CoverageChip kind="Data" pct={item.dataCoveragePct}/>
                                                    <CoverageChip kind="Analytic" pct={item.analyticCoveragePct}/>
                                                </Stack>
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Section>

                            <Section
                                sectionKey="ct"
                                title="Collection Tasks"
                                count={collectionTasks.length}
                                empty={collectionTasks.length === 0}
                            >
                                <Stack spacing={1.25}>
                                    {collectionTasks.map((item) => {
                                        const key = item.number || item.id;
                                        return (
                                            <Stack key={item.id} spacing={0.5}>
                                                <Link
                                                    component={RouterLink}
                                                    to={`/collection/tasks/${encodeURIComponent(item.number || item.id)}`}
                                                    underline="hover"
                                                    variant="body1"
                                                    fontWeight={600}
                                                >
                                                    {key} — {item.title || 'Collection Task'}
                                                </Link>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    <Chip
                                                        size="small"
                                                        variant="outlined"
                                                        color={statusColor(item.status)}
                                                        label={statusLabel(item.status)}
                                                        sx={{height: 20, fontWeight: 700, fontSize: '0.68rem'}}
                                                    />
                                                    <AuthorCell
                                                        userId={item.assigneeId}
                                                        displayName={item.assigneeName}
                                                    />
                                                </Stack>
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Section>

                            <Section
                                sectionKey="cm"
                                title="Collected Materials"
                                count={collectionMaterials.length}
                                empty={collectionMaterials.length === 0}
                            >
                                <Stack spacing={1.25}>
                                    {collectionMaterials.map((item) => {
                                        const key = item.number || item.id;
                                        return (
                                            <Stack key={item.id} spacing={0.5}>
                                                <Link
                                                    component="button"
                                                    type="button"
                                                    onClick={() => openCollectionMaterial({materialId: item.id, materialKind: 'cm'})}
                                                    underline="hover"
                                                    variant="body1"
                                                    fontWeight={600}
                                                    sx={{textAlign: 'left'}}
                                                >
                                                    {key} — {item.title || 'Collected Material'}
                                                </Link>
                                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                    {item.collectionMethod && (
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            label={item.collectionMethod}
                                                            sx={{height: 20, fontWeight: 700, fontSize: '0.68rem'}}
                                                        />
                                                    )}
                                                    <AuthorCell userId={item.authorUserId} displayName={item.author}/>
                                                </Stack>
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            </Section>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
}
